/**
 * Accounts Utility Functions
 * -----------------
 * Collection of helper functions for account-related logic.
 * This file may contain multiple utilities used across the application.
 *
 * @author  Harvinder Singh <harvinder@maropost.com>
 * @created 2025-09-25
 */

import { $accountService } from "../services/accountService";
import { constants } from './constants'

/**
 * Fetches accounts from the API
 * @returns Array of accounts
 */
export const fetchAccounts = async () => {
  await $accountService.findAll({ params: { page: 1, per_page: constants.accountsPerPage } })
  return $accountService.items.value || []
}

/**
 * Fetches roles from the API
 * @returns Array of roles
 */
export const fetchRoles = async () => {
  await $accountService.fetchRoles()
  return $accountService.roles.value || []
}

 /**
   * Extracts accountId from the current URL
   * Supports paths: /accounts/:id, /service/:id, /commerce/:id
   */
 export const fetchAccountIdFromUrl = () => {
    const match = window.location.pathname.match(/^\/(accounts|service|commerce)\/([^/]+)/)
    return match ? match[2] : ''
  }

  /**
   * Verifies account existence by fetching from API
   * Returns a verified account or empty array
   */
type Account = {
  id: number;
  name: string;
  [key: string]: any; // other properties if needed
}

export const verifyAccount = async (id: number): Promise<Account[]> => {
  await $accountService.find({ params: { id_eq: id } })
  const result = $accountService.item.value
  return result ? [result] : []
}

  /**
   * Finds an account in the given accounts array
   * If not found, verifies via API and adds it to the array
   */
  export const findAccountFromAccounts = async (accounts: Array<any>, accountId: number) => {
    let account: any = accounts.find((account) => parseInt(account.id) === accountId)

    if (!account) {
      const verifiedAccounts = await verifyAccount(accountId)
      account = verifiedAccounts?.[0]?.[0] ?? null
    }

    return account
  }